import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {ApiErrors, StoreLoadingEnum} from 'types/global';
import {createApiError} from 'helpers/global';
import {RootState} from "store";
import {SlackDto, SlackQueryParams} from 'types/slack';
import {slackApi} from "../api/slack";

interface NotificationsSlackDto {
    loading: StoreLoadingEnum;
    error: string | null;
    data: SlackDto | null;
}

const initialState: NotificationsSlackDto = {
    loading: StoreLoadingEnum.idle,
    error: null,
    data: null,
}
export const slackThunk = createAsyncThunk<
    SlackDto,
    SlackQueryParams & { signal?: AbortSignal },
    { rejectValue: ApiErrors }
>(
    'slack/get',
    async (data, thunkAPI) => {
        try {
            return await slackApi.getSlack(data, data.signal);
        } catch (e) {
            if (e.name === 'AbortError' || e.code === 'ERR_CANCELED') {
                return thunkAPI.rejectWithValue({message: 'canceled'});
            }
            return thunkAPI.rejectWithValue(createApiError(e));
        }
    }
);

export const slackSlice = createSlice({
    name: 'slack',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(slackThunk.pending, (state) => {
            state.data = null;
            state.loading = StoreLoadingEnum.pending;
            state.error = null;
        });
        builder.addCase(slackThunk.fulfilled, (state, action) => {
            state.data = action.payload;
            state.loading = StoreLoadingEnum.loaded;
            state.error = null;
        });
        builder.addCase(slackThunk.rejected, (state, action) => {
            if (!action.payload || action.payload.message === 'canceled') return;

            state.data = null;
            state.loading = StoreLoadingEnum.loaded;
            state.error = action.payload?.message || null;
        });
    },
});

export const selectSlackDataSelector = (state: RootState) => state.slack.data?.content;
export const selectSlackLoadingSelector = (state: RootState) => state.slack.loading;
export const currentOffsetSelector = (state: RootState) => state.slack.data?.pageable.offset ?? 0;
export const totalElementsSelector = (state: RootState) => state.slack.data?.totalElements ?? 0;
export const currentPageSizeSelector = (state: RootState) => state.slack.data?.pageable.pageSize ?? 0;